import Reflux from 'reflux';
import {
  StoreOverviewActions,
  StoreActions,
  AppActions,
  AuthActions
} from 'actions';
import SteelCompass from 'services/SteelCompass';
import debounce from 'oxygen-debounce';
import Config from 'Config';
import ObjectId from 'Util/ObjectId';
import ThemeStore from 'stores/Theme';
import { history } from 'helpers';
import analytics from 'helpers/universalAnalytics';
import FacebookPixel from 'helpers/facebookPixel';

const inProduction = process.env.NODE_ENV === 'production';

const isNumeric = value => !isNaN(parseFloat(value)) && isFinite(value);

const domain = /:\/\/([^\/]+)/.exec(window.location.href)[1];
let subDomain;
let storeName;
if ('//' + domain !== Config.thisHost) {
  subDomain = domain.split('.').shift();
}
if (subDomain && !isNumeric(subDomain)) {
  storeName = subDomain;
}

const loadStore = () => {
  return (
    JSON.parse(localStorage.getItem('coursio::store')) || {
      name: '',
      googleAnalyticsId: Config.googleAnalytics,
      cart: [],
      items: {},
      dialogMode: 'dialog',
      filterMode: 'simple',
      owner: {},
      presentation: {},
      countryCode: 'SE',
      theme: {
        color: 'teal'
      },
      layoutDraft: null,
      discountCode: null,
      allowedPaymentProviders: []
    }
  );
};

const persistStore = data => {
  localStorage.setItem(
    'coursio::store',
    JSON.stringify({
      name: data.name,
      googleAnalyticsId: data.googleAnalyticsId,
      facebookPixelId: data.facebookPixelId,
      cart: data.cart,
      items: data.items,
      dialogMode: data.dialogMode,
      filterMode: data.filterMode,
      countryCode: data.countryCode,
      owner: data.owner,
      presentation: data.presentation,
      theme: data.theme,
      discountCode: data.discountCode,
      layoutDraft: data.layoutDraft,
      allowedPaymentProviders: data.allowedPaymentProviders
    })
  );
};

const store = loadStore();

let stripe = null;

try {
  stripe = Stripe(Config.stripeKey);
} catch (e) {}

const StoreOverviewStore = Reflux.createStore({
  listenables: [StoreOverviewActions],
  data: {
    ...store,
    mode: 'list',
    moduleMenu: false,
    activeDiscountCount: 0,
    giftEmail: null,
    layoutHistory: [],
    layout: {
      entities: {
        sections: {},
        modules: {}
      },
      sections: [],
      overSection: {}
    },
    layoutDraft: {
      entities: {
        sections: {},
        modules: {}
      },
      sections: [],
      overSection: {}
    },
    canIEdit: false,
    canJoinForFree: false,
    docs: {},
    name: storeName,
    payment: {
      completed: false,
      errorText: {},
      loading: false,
      method: null,
      data: {}
    },
    filter: {
      searchText: null,
      tags: {},
      categoryId: null
    },
    list: [],
    selected: {},
    load: null,
    loading: true,
    stripe
  },

  init() {
    this.updateModuleDebounced = debounce(function updateModuleDebounced(
      moduleId,
      moduleProps
    ) {
      this.updateModule(moduleId, moduleProps);
    },
    500);
  },

  changeStoreItem(props) {
    const { id, type } = props;
    this.data = {
      ...this.data,
      items: {
        ...this.data.items,
        [type]: {
          ...this.data.items[type],
          [id]: {
            ...this.data.items[type][id],
            ...props
          }
        }
      }
    };
    this.trigger(this.data);
  },

  pushStoreItem(props) {
    const { id, type } = props;
    const item = this.data.items[type][id];
    const data = {
      ucpPeriod: item.ucpPeriod,
      price: item.price.net,
      videoUrl: item.videoUrl,
      presentation: item.presentation,
      id: item.id,
      public: item.public
    };
    if (type === 1) {
      return SteelCompass.request('course', 'update', data).then(
        () => {
          StoreOverviewActions.pushStoreItemComplete();
          AppActions.translatableMessage('changesSaved');
        },
        error => {
          AppActions.genericError(error);
        }
      );
    } else if (type === 2) {
      return SteelCompass.request('course-package', 'update', data).then(
        () => {
          StoreOverviewActions.pushStoreItemComplete();
          AppActions.translatableMessage('changesSaved');
        },
        error => {
          AppActions.genericError(error);
        }
      );
    }
    // return SteelCompass.call('course-package', 'update', {})
  },

  pushStoreItemComplete(data) {},

  setDraggingPos(sectionId, dropPosition, moduleId) {
    const { entities } = this.data.layoutDraft;
    let { sections, modules } = entities;
    let { drag } = modules;
    if (
      drag &&
      drag.moduleId === moduleId &&
      drag.sectionId === sectionId &&
      drag.dropPosition === dropPosition
    ) {
      return;
    }
    if (drag && drag.sectionId === sectionId) {
      const dragIndex = sections[drag.sectionId].modules.findIndex(
        _moduleId => _moduleId === 'drag'
      );
      if (dragIndex === dropPosition) {
        return;
      }
    }
    // Remove it from the old section
    if (drag) {
      Object.keys(sections).forEach(id => {
        const section = sections[id];
        if (section.modules.findIndex(_mid => _mid === 'drag') > -1) {
          sections[id] = {
            ...sections[id],
            modules: section.modules.filter(_mid => _mid !== 'drag')
          };
        }
      });
    }
    drag = {
      id: 'drag',
      sectionId,
      dropPosition,
      moduleId,
      subType: modules[moduleId].type,
      type: 'Drag'
    };

    // create a new drag handle
    modules = {
      ...modules,
      drag
    };
    // Add it to the new section
    const newModules = sections[sectionId].modules.filter(
      _moduleId => _moduleId !== 'drag'
    );
    newModules.splice(dropPosition, 0, 'drag');
    sections = {
      ...sections,
      [sectionId]: {
        ...sections[sectionId],
        modules: newModules
      }
    };
    this.data = {
      ...this.data,
      layoutDraft: {
        ...this.data.layoutDraft,
        entities: {
          ...this.data.layoutDraft.entities,
          sections,
          modules
        },
        overSection: {}
      }
    };
    this.trigger(this.data);
  },

  resetDragging() {
    const { entities } = this.data.layoutDraft;
    this.data = {
      ...this.data,
      layoutDraft: {
        ...this.data.layoutDraft,
        overSection: {},
        entities: {
          ...entities,
          modules: {
            ...entities.modules,
            drag: null,
            overRow: {}
          }
        }
      }
    };
    this.trigger(this.data);
  },

  overSection(position, id) {
    const { overSection } = this.data.layoutDraft;
    if (overSection.id !== id || overSection.position !== position) {
      const { entities } = this.data.layoutDraft;
      let { sections } = entities;
      const { drag } = entities.modules;
      if (drag) {
        sections = {
          ...sections,
          [drag.sectionId]: {
            ...sections[drag.sectionId],
            modules: sections[drag.sectionId].modules.filter(
              moduleId => moduleId !== 'drag'
            )
          }
        };
      }
      this.data = {
        ...this.data,
        layoutDraft: {
          ...this.data.layoutDraft,
          entities: {
            ...entities,
            sections,
            modules: {
              ...entities.modules,
              drag: null
            }
          },
          overSection: {
            position,
            id
          }
        }
      };

      this.trigger(this.data);
    }
  },

  moveToNewSection(toSectionId, fromSectionId, moduleId) {
    if (toSectionId === fromSectionId) {
      return;
    }
    // TODO : move whole section
    const { entities, overSection } = this.data.layoutDraft;
    const sections = [...this.data.layoutDraft.sections];
    const offset = overSection.position === 'after' ? 1 : 0;
    if (entities.sections[fromSectionId].modules.length === 1) {
      this.moveSection(fromSectionId, toSectionId, offset);
      return;
    }
    const sectionId = new ObjectId().toString();
    const section = {
      id: sectionId,
      type:
        entities.modules[moduleId].type === 'Banner' ||
        entities.modules[moduleId].type === 'List'
          ? 'banner'
          : 'default',
      modules: [moduleId]
    };
    const dropSectionIndex = sections.findIndex(
      _sectionId => overSection.id === _sectionId
    );
    sections.splice(dropSectionIndex + offset, 0, sectionId);
    this.data = {
      ...this.data,
      layoutDraft: {
        ...this.data.layoutDraft,
        overSection: {}
      }
    };
    this.saveSnapshot();
    this.data = {
      ...this.data,
      layoutDraft: {
        ...this.data.layoutDraft,
        entities: {
          ...entities,
          sections: {
            ...entities.sections,
            [fromSectionId]: {
              ...entities.sections[fromSectionId],
              modules: entities.sections[fromSectionId].modules.filter(
                id => id !== moduleId
              )
            },
            [sectionId]: section
          }
        },
        sections,
        overSection: {}
      }
    };
    if (
      this.data.layoutDraft.entities.sections[fromSectionId].modules.length < 1
    ) {
      delete this.data.layoutDraft.entities.sections[fromSectionId];
      this.data.layoutDraft.sections = this.data.layoutDraft.sections.filter(
        id => id !== fromSectionId
      );
    }
    this.trigger(this.data);
  },

  resetOverSection() {
    this.data = {
      ...this.data,
      layoutDraft: {
        ...this.data.layoutDraft,
        overSection: {}
      }
    };
    this.trigger(this.data);
  },

  endDragging(sectionId, item) {
    const { entities, overSection } = this.data.layoutDraft;
    const { drag } = entities.modules;
    if (!drag && overSection && overSection.id) {
      return this.moveToNewSection(overSection.id, sectionId, item.id);
    }
    let { sections, modules } = entities;
    if (drag) {
      const { sectionId: toSectionId, moduleId } = drag;
      modules = {
        ...modules,
        drag: null
      };
      this.data = {
        ...this.data,
        layoutDraft: {
          ...this.data.layoutDraft,
          overSection: {},
          entities: {
            ...entities,
            modules
          }
        }
      };
      this.saveSnapshot();

      if (sectionId === toSectionId) {
        const section = {
          ...sections[sectionId],
          modules: sections[sectionId].modules
            .filter(_mId => _mId !== moduleId)
            .map(id => {
              if (id === 'drag') {
                return moduleId;
              }
              return id;
            })
        };
        sections = {
          ...sections,
          [sectionId]: section
        };
      } else {
        const section = {
          ...sections[sectionId],
          modules: sections[sectionId].modules.filter(_mId => _mId !== moduleId)
        };
        const toSection = {
          ...sections[toSectionId],
          modules: sections[toSectionId].modules.map(id => {
            if (id === 'drag') {
              return moduleId;
            }
            return id;
          })
        };
        sections = {
          ...sections,
          [sectionId]: section,
          [toSectionId]: toSection
        };
      }
      this.data = {
        ...this.data,
        layoutDraft: {
          ...this.data.layoutDraft,
          entities: {
            ...entities,
            sections
          }
        }
      };
      if (sections[sectionId].modules.length < 1) {
        this.data.layoutDraft.sections = this.data.layoutDraft.sections.filter(
          id => id !== sectionId
        );
        delete sections[sectionId];
      }
      this.trigger(this.data);
    }
  },

  getInitialState() {
    return this.data;
  },

  setFilterCategory(categoryId) {
    if (this.data.filter.categoryId !== categoryId) {
      const filter = {
        ...this.data.filter,
        categoryId
      };
      this.data = {
        ...this.data,
        filter
      };
      this.trigger(this.data);
    }
  },

  setFilterTag(tagName, enabled = true) {
    if (!!this.data.filter.tags[tagName] !== enabled) {
      const filter = {
        ...this.data.filter,
        tags: {
          ...this.data.filter.tags,
          [tagName]: enabled
        }
      };
      this.data = {
        ...this.data,
        filter
      };
      this.trigger(this.data);
    }
  },

  setSearchText(searchText) {
    if (this.data.filter.searchText !== searchText) {
      const filter = {
        ...this.data.filter,
        searchText
      };
      this.data = {
        ...this.data,
        filter
      };
      this.trigger(this.data);
    }
  },

  setFilter(filter) {
    this.data = {
      ...this.data,
      filter: {
        ...this.data.filter,
        ...filter
      }
    };
    this.trigger(this.data);
  },

  editMode() {
    let { layoutDraft } = this.data;
    if (!layoutDraft) {
      layoutDraft = JSON.parse(JSON.stringify(this.data.layout));
    }
    this.data = {
      ...this.data,
      layoutDraft: {
        ...layoutDraft,
        selectedModuleId: null,
        selectedSectionId: null
      },
      mode: 'editor'
    };
    this.trigger(this.data);
  },

  listMode() {
    this.data = {
      ...this.data,
      mode: 'list'
    };
    this.trigger(this.data);
  },

  start(name, type = null, slug = null) {
    if (slug && type) {
      this.data.load = {
        type,
        slug
      };
    }
    if (this.data.name !== name) {
      StoreOverviewActions.read(name);
    } else {
      this.loadQueued();
    }
  },

  setDialogMode(dialogMode) {
    this.data = {
      ...this.data,
      dialogMode
    };
    persistStore(this.data);
    this.trigger(this.data);
  },

  loading(loading = true) {
    this.data = {
      ...this.data,
      loading
    };
    this.trigger(this.data);
  },

  read(name, skipItems = false) {
    this.loading();
    const { discountCode } = this.data;
    return SteelCompass.request('store', 'read', {
      name,
      hostname: domain,
      discountCode,
      skipItems
    }).then(
      store => {
        if (store && store.owner && store.owner.locale) {
          AuthActions.setLocale(store.owner.locale);
        }
        StoreOverviewActions.readComplete(name, store);
      },
      error => {
        this.loading(false);
        AppActions.genericError(error);
      }
    );
  },

  getEntities(data) {
    const list = [];
    const items = {};
    data.forEach(item => {
      item.tagsMap = Array.isArray(item.tagsMap) ? {} : item.tagsMap;
      const categories = {};
      if (!Array.isArray(item.categories)) {
        categories[item.categories] = true;
      } else {
        item.categories.forEach(category => (categories[category] = true));
      }
      item.categories = categories;
      items[item.type] = items[item.type] || {};
      items[item.type][item.id] = item;
      list.push({ type: item.type, id: item.id });
    });
    return { list, items };
  },

  getDefaultLayout(data) {
    const bannerModule = {
      type: 'Banner',
      src: data.presentation.coverUrl,
      color: ThemeStore.getColor(data.theme.color),
      headerPosition: 'centerCenter',
      backgroundColor: '#FFFFFF',
      opacity: 0,
      size: 2,
      header: `<h1>${data.presentation.text}</h1>`,
      id: new ObjectId().toString()
    };
    const listModule = {
      type: 'List',
      id: new ObjectId().toString(),
      mode: 'cards'
    };
    const bannerSection = {
      type: 'banner',
      id: new ObjectId().toString(),
      modules: [bannerModule.id]
    };
    const listSection = {
      type: 'banner',
      id: new ObjectId().toString(),
      modules: [listModule.id]
    };
    return {
      entities: {
        sections: {
          [bannerSection.id]: bannerSection,
          [listSection.id]: listSection
        },
        modules: {
          [bannerModule.id]: bannerModule,
          [listModule.id]: listModule
        }
      },
      sections: [bannerSection.id, listSection.id]
    };
  },

  readComplete(name, store) {
    const { items, layoutStructure, ...other } = store;
    if (
      layoutStructure &&
      layoutStructure.entities &&
      layoutStructure.entities.modules
    ) {
      delete layoutStructure.entities.modules.drag;
    }
    const entities = this.getEntities(items);
    let defaultLayout;
    const template = {
      entities: {
        sections: {},
        modules: {}
      },
      sections: [],
      overSection: {}
    };
    if (!layoutStructure) {
      defaultLayout = this.getDefaultLayout(store);
    }
    const googleAnalyticsId =
      inProduction && store.ga ? store.ga.id : Config.googleAnalyticsId;
    analytics.initialize(googleAnalyticsId);

    if (store.owner.id === 100205) {
      this.renderGtmScript('GTM-5XL3C2J');
    }

    if (store.owner.id === 141243) {
      this.renderGtmScript('GTM-WFLZ5BN');
    }

    const options = {
      autoConfig: true,
      debug: false
    };
    const facebookPixelId =
      inProduction && store.js ? store.js.pixel : Config.facebookPixelId;
    FacebookPixel.init(facebookPixelId, null, options);
    FacebookPixel.track('PageView');

    this.data = {
      ...this.data,
      ...other,
      ...entities,
      layout: {
        ...template,
        ...defaultLayout,
        ...layoutStructure
      },
      name,
      googleAnalyticsId,
      facebookPixelId,
      loading: false
    };
    persistStore(this.data);

    this.trigger(this.data);
    this.loadQueued();
  },

  update() {
    const { layoutDraft } = this.data;
    persistStore(this.data);
    StoreOverviewActions.updateComplete(layoutDraft);
  },

  publish() {
    const { name, layoutDraft } = this.data;
    return SteelCompass.request('store', 'saveLayout', {
      name,
      data: layoutDraft
    }).then(
      data => {
        StoreOverviewActions.updateComplete(layoutDraft);
      },
      error => {
        this.loading(false);
        AppActions.genericError(error);
      }
    );
  },

  discard() {
    this.saveSnapshot();
    this.data = {
      ...this.data,
      layoutDraft: {
        ...this.data.layout
      },
      mode: 'list'
    };
    this.trigger(this.data);
  },

  updateComplete(layout) {
    this.data = {
      ...this.data,
      layout,
      layoutDraft: {
        ...this.data.layout
      },
      mode: 'list'
    };
    this.trigger(this.data);
  },

  loadQueued() {
    if (this.data.load) {
      const { type, slug } = this.data.load;
      StoreOverviewActions.findBySlug(type, slug);
      this.data.load = null;
    }
  },

  toggleFilterMode() {
    const filterMode =
      this.data.filterMode === 'simple' ? 'extended' : 'simple';
    this.data = {
      ...this.data,
      filterMode
    };
    persistStore(this.data);
    this.trigger(this.data);
  },

  completePayment(paymentId, providerId, token) {
    SteelCompass.request('payment', 'complete', {
      paymentId,
      providerId,
      token
    }).then(
      data => {
        if (data) {
          StoreOverviewActions.paymentCompleted(data);
        } else {
          AppActions.genericError();
        }
      },
      error => {
        if (error == '3D authentication has failed.') {
          this.data = {
            ...this.data,
            payment: {
              ...this.data.payment,
              loading: false,
              completed: false,
              error,
              data: {
                ...this.data.payment.data,
                url: null,
                error
              }
            }
          };
          this.trigger(this.data);
        } else {
          AppActions.genericError(error);
        }
      }
    );
  },

  startPayment(providerId, token, evidence, address, source) {
    // gift email
    const { giftEmail, discountCode, cart, name, payment } = this.data;
    this.data = {
      ...this.data,
      payment: {
        ...payment,
        loading: true,
        completed: false,
        error: null
      }
    };
    const errorLookup = {
      incorrect_number: 'number',
      card_declined: 'number',
      invalid_number: 'number',
      invalid_expiry_year: 'year',
      invalid_expiry_month: 'month',
      invalid_cvc: 'cvc',
      '0': 'number'
    };
    this.trigger(this.data);
    SteelCompass.request('payment', 'start', {
      address,
      gifterEmail: giftEmail,
      source,
      evidence,
      cart,
      providerId,
      storeId: name,
      discountCode,
      token
    }).then(
      data => {
        if (data) {
          if (
            data.snippet ||
            (data.action && data.action == 'redirect' && data.url)
          ) {
            StoreOverviewActions.paymentStarted(data);
          } else if (providerId === 6) {
            this.data.stripe.redirectToCheckout({
              sessionId: data.sessionId
            });
          } else {
            StoreOverviewActions.paymentCompleted(data);
          }
        } else {
          this.data = {
            ...this.data,
            payment: {
              ...this.data.payment,
              loading: false,
              completed: false,
              errorText: {},
              error: true
            }
          };
          this.trigger(this.data);
        }
      },
      error => {
        const match = error.match(/(Stripe error:)\s(.*)\s\(#(.*)\)/) || [];
        if (match.length < 1) {
          this.data = {
            ...this.data,
            payment: {
              ...this.data.payment,
              loading: false
            }
          };
          this.trigger(this.data);
          return AppActions.genericError(error);
        }
        const [str, type, message, messageId] = match;
        let errorText = {};
        if (type === 'Stripe error:') {
          const field = errorLookup[messageId];
          if (field) {
            errorText[field] = message;
          }
        }
        this.data = {
          ...this.data,
          payment: {
            ...this.data.payment,
            loading: false,
            error,
            errorText
          }
        };
        this.trigger(this.data);
      }
    );
  },
  clearError() {
    this.data = {
      ...this.data,
      payment: {
        ...this.data.payment,
        errorText: {}
      }
    };
    this.trigger(this.data);
  },

  clearPaymentMethod() {
    this.setPaymentMethod(null);
  },

  resetPayment() {
    this.data = {
      ...this.data,
      giftEmail: null,
      payment: {
        ...this.data.payment,
        method: null,
        completed: false
      }
    };
    this.trigger(this.data);
  },

  reset() {
    this.data = {
      ...this.data,
      giftEmail: null,
      cart: [],
      payment: {
        ...this.data.payment,
        method: null,
        completed: false
      }
    };
    localStorage.removeItem('coursio::store');
    this.trigger(this.data);
  },

  setPaymentMethod(method) {
    this.data = {
      ...this.data,
      payment: {
        ...this.data.payment,
        method
      }
    };
    this.trigger(this.data);
  },

  paymentCompleted(data) {
    this.data = {
      ...this.data,
      payment: {
        ...this.data.payment,
        loading: false,
        completed: true,
        error: false,
        data: {
          ...this.data.payment.data,
          ...data
        }
      }
    };
    const { googleAnalyticsId, facebookPixelId } = this.data;
    if (googleAnalyticsId) {
      const [{ type, id, gift }] = this.data.cart;
      const {
        owner: { username: affiliation }
      } = this.data;
      const item = this.data.items[type][id];
      const options = {
        autoConfig: true,
        debug: false
      };
      FacebookPixel.init(facebookPixelId, null, options);
      FacebookPixel.track('Purchase', {
        currency: item.currency || 'SEK',
        value: item.price.discounted
      });
      analytics.initialize(googleAnalyticsId);
      analytics.ecAddTransaction({
        transaction_id: `${type}-${id}`,
        affiliation,
        value: item.price.discounted,
        currency: item.currency || 'SEK',
        items: [
          {
            id,
            name: item.name,
            quantity: 1,
            price: item.price.discounted
          }
        ]
      });
    } else {
      const [{ type, id, gift }] = this.data.cart;
      const {
        owner: { username: affiliation }
      } = this.data;
      const item = this.data.items[type][id];
      const options = {
        autoConfig: true,
        debug: false
      };
      FacebookPixel.init(facebookPixelId, null, options);
      FacebookPixel.track('Purchase', {
        currency: item.currency || 'SEK',
        value: item.price.discounted
      });
      analytics.initialize();
      analytics.ecAddTransaction({
        transaction_id: `${type}-${id}`,
        affiliation,
        value: item.price.discounted,
        currency: item.currency || 'SEK',
        items: [
          {
            id,
            name: item.name,
            quantity: 1,
            price: item.price.discounted
          }
        ]
      });
    }
    this.trigger(this.data);
  },

  paymentStarted(data) {
    const item = this.data.items[this.data.cart[0].type][this.data.cart[0].id];
    analytics.checkout({
      id: item.id,
      name: item.name,
      price: item.price.discounted,
      quantity: 1
    });
    this.data = {
      ...this.data,
      payment: {
        ...this.data.payment,
        loading: false,
        completed: false,
        error: false,
        data: {
          ...this.data.payment.data,
          ...data
        }
      }
    };
    persistStore(this.data);
    this.trigger(this.data);
  },

  findBySlug(type, slug) {
    const { discountCode, name } = this.data;
    return SteelCompass.request('store', 'findBySlug', {
      type,
      slug,
      discountCode,
      username: name
    }).then(
      data => {
        StoreOverviewActions.findBySlugComplete(data);
      },
      error => {
        AppActions.genericError(error);
      }
    );
  },

  addCart(type, id, name, gift = false) {
    this.data = {
      ...this.data,
      name,
      cart: [{ type, id, gift }]
    };
    persistStore(this.data);
    this.trigger(this.data);
  },

  addToCart(data, gift = false) {
    const { type, id, name, price } = data;
    this.data = {
      ...this.data,
      cart: [{ type, id, gift }]
    };
    persistStore(this.data);
    analytics.ecAddItem({
      name: name,
      id,
      variant: gift ? 'gift' : null,
      category: type,
      price: price.discounted,
      quantity: 1
    });
    FacebookPixel.track('AddToCart', {
      content_name: this.data.name,
      contents: [{ type, id, gift }]
    });
    this.trigger(this.data);
    !gift && history.push(`/checkout/${this.data.name}/${id}/${type}`);
  },

  clearCart() {
    this.data = {
      ...this.data,
      cart: [],
      payment: {
        ...this.data.payment,
        completed: false,
        method: null
      }
    };
    this.trigger(this.data);
  },

  findBySlugComplete(data) {
    console.log(data);
    const { type, id } = data;
    const { items } = this.data;
    const item = {
      ...items[type][id],
      ...data
    };
    this.data = {
      ...this.data,
      items: {
        ...items,
        [type]: {
          ...items[type],
          [id]: item
        }
      },
      selected: { type, id }
    };
    persistStore(this.data);
    analytics.viewitem({
      id: data.id,
      name: data.name,
      price: data.price.discounted,
      quantity: 1
    });
    this.trigger(this.data);
  },

  clearSelected() {
    this.data = {
      ...this.data,
      selected: {}
    };
    this.trigger(this.data);
  },

  setDiscountCode(discountCode) {
    this.data = {
      ...this.data,
      discountCode
    };
    persistStore(this.data);
    this.trigger(this.data);
  },

  setGiftEmail(giftEmail) {
    this.data = {
      ...this.data,
      giftEmail
    };
    const [{ type, id }] = this.data.cart;
    this.trigger(this.data);
    history.push(`/checkout/${this.data.name}/${id}/${type}`);
  },

  openModuleMenu() {
    this.data = {
      ...this.data,
      moduleMenu: true
    };
    this.trigger(this.data);
  },

  closeModuleMenu() {
    this.data = {
      ...this.data,
      moduleMenu: false
    };
    this.trigger(this.data);
  },

  addModule(sectionProps, moduleProps) {
    let sectionId = new ObjectId().toString();
    const moduleId = new ObjectId().toString();
    let sections;
    const module = {
      ...moduleProps,
      id: moduleId
    };

    let section;

    const { layoutDraft } = this.data;

    if (layoutDraft.selectedSectionId) {
      const selectedSection =
        layoutDraft.entities.sections[layoutDraft.selectedSectionId];
      sectionId = layoutDraft.selectedSectionId;
      section = {
        ...selectedSection,
        modules: [...selectedSection.modules, moduleId]
      };
      sections = layoutDraft.sections;
    } else {
      section = {
        type: 'banner',
        id: sectionId,
        ...sectionProps,
        modules: [moduleId]
      };
      sections = [...layoutDraft.sections, sectionId];
    }

    this.saveSnapshot();
    this.data = {
      ...this.data,
      moduleMenu: false,
      layoutDraft: {
        ...layoutDraft,
        ...this.data.layoutDraft,
        entities: {
          sections: {
            ...layoutDraft.entities.sections,
            [sectionId]: section
          },
          modules: {
            ...layoutDraft.entities.modules,
            [moduleId]: module
          }
        },
        sections
      }
    };
    this.trigger(this.data);
  },

  selectSection(selectedSectionId) {
    this.data = {
      ...this.data,
      layoutDraft: {
        ...this.data.layoutDraft,
        selectedSectionId
      }
    };
    this.trigger(this.data);
  },

  selectModule(selectedModuleId) {
    this.data = {
      ...this.data,
      layoutDraft: {
        ...this.data.layoutDraft,
        selectedModuleId
      }
    };
    this.trigger(this.data);
  },

  unselect() {
    this.data = {
      ...this.data,
      layoutDraft: {
        ...this.data.layoutDraft,
        selectedModuleId: null,
        selectedSectionId: null
      }
    };
    this.trigger(this.data);
  },

  moveElement(array, from, to) {
    const defCopy = [...array];
    let dest = to;
    if (from < to) {
      dest = dest - 1;
    }
    if (to >= defCopy.length) {
      let k = to - defCopy.length;
      while (k-- + 1) {
        defCopy.push(undefined);
      }
    }
    defCopy.splice(dest, 0, defCopy.splice(from, 1)[0]);
    return defCopy.filter(item => typeof item !== 'undefined');
  },

  moveSection(sectionId, toSectionId, offset) {
    this.data = {
      ...this.data,
      layoutDraft: {
        ...this.data.layoutDraft,
        overSection: {}
      }
    };
    this.saveSnapshot();
    const { layoutDraft } = this.data;
    const { sections } = layoutDraft;
    const index = sections.findIndex(id => sectionId === id);
    const destIndex = sections.findIndex(id => toSectionId === id);
    if (index > -1) {
      this.data = {
        ...this.data,
        layoutDraft: {
          ...layoutDraft,
          sections: this.moveElement(
            layoutDraft.sections,
            index,
            Math.max(destIndex + offset, 0)
          )
        }
      };
      this.trigger(this.data);
    }
  },

  updateModule(moduleId, moduleProps) {
    this.saveSnapshot();
    const { layoutDraft } = this.data;
    this.data = {
      ...this.data,
      layoutDraft: {
        ...layoutDraft,
        entities: {
          ...layoutDraft.entities,
          modules: {
            ...layoutDraft.entities.modules,
            [moduleId]: {
              ...layoutDraft.entities.modules[moduleId],
              ...moduleProps
            }
          }
        }
      }
    };
    this.trigger(this.data);
  },

  updateSection(sectionId, sectionProps) {
    this.saveSnapshot();
    const { layoutDraft } = this.data;
    this.data = {
      ...this.data,
      layoutDraft: {
        ...layoutDraft,
        entities: {
          ...layoutDraft.entities,
          sections: {
            ...layoutDraft.entities.sections,
            [sectionId]: {
              ...layoutDraft.entities.sections[sectionId],
              ...sectionProps
            }
          }
        }
      }
    };
    this.trigger(this.data);
  },

  undo() {
    this.updateModuleDebounced.clear();
    const layoutDraft = this.data.layoutHistory.pop();
    this.data = {
      ...this.data,
      layoutDraft: {
        ...layoutDraft
      }
    };
    this.trigger(this.data);
  },

  saveSnapshot() {
    this.data.layoutHistory.push({ ...this.data.layoutDraft });
  },

  deleteModule() {
    this.saveSnapshot();
    const { selectedModuleId, selectedSectionId } = this.data.layoutDraft;
    const layoutDraft = {
      ...this.data.layoutDraft
    };

    const sections = {
      ...layoutDraft.entities.sections
    };
    const modules = {
      ...layoutDraft.entities.modules
    };
    delete modules[selectedModuleId];
    sections[selectedSectionId] = {
      ...sections[selectedSectionId],
      modules: sections[selectedSectionId].modules.filter(
        moduleId => moduleId !== selectedModuleId
      )
    };
    if (sections[selectedSectionId].modules.length < 1) {
      delete sections[selectedSectionId];
      layoutDraft.sections = layoutDraft.sections.filter(
        sectionId => sectionId !== selectedSectionId
      );
    }

    this.data = {
      ...this.data,
      layoutDraft: {
        ...layoutDraft,
        entities: {
          ...layoutDraft.entities,
          sections,
          modules
        }
      },
      selectedSectionId: null,
      selectedModuleId: null
    };
    this.trigger(this.data);
  },

  renderGtmScript(gtmCode) {
    return (function(w, d, s, l, i) {
      w[l] = w[l] || [];
      w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
      var f = d.getElementsByTagName(s)[0],
        j = d.createElement(s),
        dl = l != 'dataLayer' ? '&l=' + l : '';
      j.async = true;
      j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
      f.parentNode.insertBefore(j, f);
    })(window, document, 'script', 'dataLayer', gtmCode);
  }
});

export default StoreOverviewStore;



// WEBPACK FOOTER //
// ./src/js/stores/StoreOverview.js